import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleCheckBig, Loader2, LockKeyhole } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';

import useAuthStore from 'store/auth';
import ApprovedImage from 'assets/images/card-approved.png';
import { verifyDashboardHSBCCard } from 'services/auth/api';

import HSBCLogo from 'assets/hsbc-logo.png';
import { getAccountStatus } from 'services/dashboard/api';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

type HSBCVerificationFormData = {
  cardNumber: string;
};

export default function HSBCCardVerificationDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const profileId = useAuthStore((store) => store.currentUserProfileId);
  const setAccountStatus = useAuthStore((store) => store.setAccountStatus);

  const [isVerified, setIsVerified] = useState(false);
  const [isFetching, setIsFetching] = useState(false)

  const form = useForm<HSBCVerificationFormData>({
    mode: 'onBlur',
    defaultValues: {
      cardNumber: '',
    },
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: verifyDashboardHSBCCard,
  });

  async function onSubmit(values: HSBCVerificationFormData) {
    try {
      await mutateAsync({
        hsbc_card_number: values.cardNumber,
      });
      setIsVerified(true);
    } catch (error) {
      console.log("🚀 ~ onSubmit ~ error:", error)
      if (error instanceof AxiosError) {
        form.setError('cardNumber', {
          type: 'manual',
          message: error.response?.data.message || t('hsbc_card_not_eligible_msg'),
        });
      }
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setIsVerified(false);
      form.reset({ cardNumber: '' });
    }
  };

  const onContinue = async () => {
    setIsFetching(true);
    try {
      const status = await getAccountStatus();
      setAccountStatus(status);

      queryClient.invalidateQueries({ queryKey: ['dashboard-flags', profileId] });
      handleOpenChange(false);
    } catch (error) {
      toast.error(t('something_went_wrong'));
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-w-lg'>
        {isVerified ? (
          <div className='flex flex-col items-center w-full space-y-6'>
            <div className='flex items-center min-w-0 gap-2'>
              <img
                src={HSBCLogo}
                alt='HSBC'
                className='w-auto h-5 shrink-0'
              />
              <DialogTitle className='min-w-0 text-base font-semibold leading-tight truncate sm:text-lg'>
                {t('hsbc_card_verified_title')}
              </DialogTitle>
            </div>

            <div className='h-32 aspect-square'>
              <img
                src={ApprovedImage}
                alt='card-approved'
                className='object-contain w-full h-full'
              />
            </div>

            <div className='flex items-center justify-center w-full gap-2 py-2 bg-green-100 rounded-3xl'>
              <CircleCheckBig className='text-green-500 size-4' />
              <p className='text-sm font-medium'>{t('you_re_approved')}</p>
            </div>

            <p className='mx-8 text-sm text-center text-muted-foreground'>{t('hsbc_card_verified_msg')}</p>

            <Button
              type='button'
              className='w-full px-6 font-semibold rounded-full'
              onClick={onContinue}
              disabled={isFetching}
            >
              {isFetching && <Loader2
                role='progressbar'
                className='w-4 h-4 mr-2 animate-spin'
              />}
              {t('continue')}
            </Button>
          </div>
        ) : (
          <div className='space-y-6'>
            <DialogHeader>
              <div className='flex items-center min-w-0 gap-2'>
                <img
                  src={HSBCLogo}
                  alt='HSBC'
                  className='w-auto h-5 shrink-0'
                />
                <DialogTitle className='min-w-0 text-base font-semibold leading-tight truncate sm:text-lg'>
                  {t('hsbc_card_verification_dialog_title')}
                </DialogTitle>
              </div>
            </DialogHeader>

            <div>
              <h2 className='text-lg font-medium'>{t('hsbc_verification_title')}</h2>
              <p className='text-sm text-muted-foreground'>{t('hsbc_verification_info')}</p>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='w-full space-y-5'
              >
                <FormField
                  control={form.control}
                  name='cardNumber'
                  rules={{
                    required: t('hsbc_card_not_eligible_msg'),
                    minLength: {
                      value: 6,
                      message: t('hsbc_card_not_eligible_msg'),
                    },
                    maxLength: {
                      value: 6,
                      message: t('hsbc_card_not_eligible_msg'),
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('hsbc_verification_input_label')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          inputMode='numeric'
                          autoComplete='one-time-code'
                          onChange={(e) => {
                            const { value } = e.target;
                            const numericRegex = /^\d{0,6}$/;
                            if (numericRegex.test(value)) {
                              field.onChange(value);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                      <FormDescription className='flex items-start gap-1 mt-2'>
                        <LockKeyhole className='w-4 h-4 shrink-0' />
                        {t('hsbc_card_verification_privacy_info')}
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <div className='flex justify-center gap-3'>
                  <Button type='submit' disabled={isPending}>
                    {isPending ? (
                      <Loader2
                        role='progressbar'
                        className='w-4 h-4 mr-2 animate-spin'
                      />
                    ) : null}
                    {t('verify_and_continue')}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}